import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import paginationStyles from "../../components/common/Pagination.module.css";
import Dropdown from "../../components/common/Dropdown";
import { getQueueAI } from "../../api/queueAI";
import { getCompaniesAdmin } from "../../api/administration";
import { QueueAI, Company } from "../../types/types";
import { useNotification } from "../../context/NotificationContext";
import adminSurface from "../../components/layouts/AdminSurface.module.css";
import studio from "../../styles/studioPage.module.css";

const QueueAILogs: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();
    const [searchParams] = useSearchParams();

    const [queueAILogs, setQueueAILogs] = useState<QueueAI[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [companiesLoading, setCompaniesLoading] = useState<boolean>(true);
    const [selectedCompany, setSelectedCompany] = useState<string>(() => searchParams.get("companyId") || "");
    const [page, setPage] = useState<number>(1);
    const [limit] = useState<number>(50);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number>(0);

    useEffect(() => {
        const fromUrl = searchParams.get("companyId") || "";
        setSelectedCompany(fromUrl);
    }, [searchParams]);

    // Fetch companies on component mount
    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                setCompaniesLoading(true);
                const token = localStorage.getItem("token");
                if (!token) {
                    console.error("Token not found");
                    return;
                }
                const response = await getCompaniesAdmin(token);
                setCompanies(response);
            } catch (error) {
                console.error("Error fetching companies:", error);
                showNotification({
                    message: t("errorFetchingCompanies") || "Error fetching companies",
                    type: "error",
                });
            } finally {
                setCompaniesLoading(false);
            }
        };

        fetchCompanies();
    }, [showNotification, t]);

    // Reset page when filter changes
    useEffect(() => {
        setPage(1);
    }, [selectedCompany]);

    // Fetch QueueAI logs
    const fetchQueueAILogs = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No token found");
                return;
            }

            const response = await getQueueAI({
                token,
                page,
                limit,
                companyId: selectedCompany || undefined,
            });

            setQueueAILogs(response.data);
            setTotalCount(response.pagination.totalCount);
            setTotalPages(response.pagination.totalPages);
        } catch (error) {
            console.error("Error fetching QueueAI logs:", error);
            showNotification({
                message: t("errorFetchingQueueAILogs") || "Error fetching QueueAI logs",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    }, [page, limit, selectedCompany, showNotification, t]);

    // Fetch QueueAI logs when dependencies change
    useEffect(() => {
        fetchQueueAILogs();
    }, [fetchQueueAILogs]);

    // Company options for dropdown
    const companyOptions = useMemo(() => {
        return companies.map((company) => ({
            value: company.id,
            label: company.name,
        }));
    }, [companies]);

    // Pagination logic
    const visiblePages = useMemo(() => {
        const range = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) range.push(i);
        } else {
            range.push(1);
            if (page > 3) range.push("...");
            for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
                range.push(i);
            }
            if (page < totalPages - 2) range.push("...");
            range.push(totalPages);
        }
        return range;
    }, [page, totalPages]);

    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, totalCount);

    // Helper function to get the type display name
    const getTypeDisplayName = (queueAI: QueueAI) => {
        if (queueAI.queueAIText) return t("textGeneration") || "Text Generation";
        if (queueAI.queueAIAudio) return t("audioGeneration") || "Audio Generation";
        if (queueAI.queueAIBlog) return t("blogGeneration") || "Blog Generation";
        if (queueAI.queueAIProjectTranslation) return t("projectTranslation") || "Project Translation";
        if (queueAI.queueAITrack) return t("trackProcessing") || "Track Processing";
        if (queueAI.queueAIStandaloneText) return t("standaloneText") || "Standalone Text";
        if (queueAI.queueAINotificationTexts) return t("notificationTexts") || "Notification Texts";
        if (queueAI.queueAIRemix) return t("textRemix") || "Text Remix";
        return queueAI.type || t("unknown") || "Unknown";
    };

    // Helper function to get additional info based on type
    const getAdditionalInfo = (queueAI: QueueAI) => {
        if (queueAI.queueAIText) {
            return `${queueAI.queueAIText.numberOfWords} ${t("words") || "words"}, ${queueAI.queueAIText.audience}`;
        }
        if (queueAI.queueAIAudio) {
            return `${t("dialog") || "Dialog"}: ${
                queueAI.queueAIAudio.isDialog ? t("yes") || "Yes" : t("no") || "No"
            }, ${t("speed") || "Speed"}: ${queueAI.queueAIAudio.audioSpeed}`;
        }
        if (queueAI.queueAIBlog) {
            return `${t("tone") || "Tone"}: ${queueAI.queueAIBlog.desiredTone}, ${
                t("audience") || "Audience"
            }: ${queueAI.queueAIBlog.audience}`;
        }
        if (queueAI.queueAIProjectTranslation) {
            return `${t("text") || "Text"}: ${
                queueAI.queueAIProjectTranslation.isText ? t("yes") || "Yes" : t("no") || "No"
            }, ${t("audio") || "Audio"}: ${
                queueAI.queueAIProjectTranslation.isAudio ? t("yes") || "Yes" : t("no") || "No"
            }`;
        }
        return "";
    };

    const getLanguageAI = (queueAI: QueueAI) => {
        if (queueAI.queueAIText) return `${queueAI.queueAIText.languageAI?.name}`;
        if (queueAI.queueAIProjectTranslation) return `${queueAI.queueAIProjectTranslation.languagesAI?.name}`;
    };

    return (
        <div className={adminSurface.pageContent}>
            <div className={`${studio.pageTitle} ${adminSurface.pageTitle}`}>
                <TitleSubtitle
                    title={t("queueAILogs") || "QueueAI Logs"}
                    subtitle={t("queueAILogsSubtitle") || "View and monitor QueueAI processing logs"}
                />
            </div>

            <div className={adminSurface.supportFbar}>
                <div className={adminSurface.supportFbarTitle}>{t("filterLogs") || "Filter Logs"}</div>
                <Dropdown
                    label=""
                    options={companyOptions}
                    value={selectedCompany}
                    onChange={setSelectedCompany}
                    placeholder={t("filterByCompany") || "Filter by company"}
                    loading={companiesLoading}
                    error={false}
                    isWithoutMargin
                />
            </div>

            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                </div>
            ) : queueAILogs.length === 0 ? (
                <div className={`${adminSurface.tableContainer} ${adminSurface.tblScrollSupport}`}>
                    <div className={adminSurface.supportEmpty}>
                        {t("noQueueAILogsFound") || "No QueueAI logs found"}
                    </div>
                </div>
            ) : (
                <>
                    <div
                        className={`${adminSurface.queueaiTableContainer} ${adminSurface.tableContainer} ${adminSurface.tblScrollSupport}`}
                    >
                        <table className={adminSurface.table}>
                            <thead>
                                <tr>
                                    <th>{t("company") || "Company"}</th>
                                    <th>{t("type") || "Type"}</th>
                                    <th>{t("status") || "Status"}</th>
                                    <th>{t("admin") || "Admin"}</th>
                                    <th>{t("additionalInfo") || "Additional Info"}</th>
                                    <th>{t("language") || "Language"}</th>
                                    <th>{t("createdAt") || "Created At"}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {queueAILogs.map((queueAI) => (
                                    <tr key={queueAI.id}>
                                        <td>{queueAI.company?.name || t("notAvailable") || "N/A"}</td>
                                        <td>{getTypeDisplayName(queueAI)}</td>
                                        <td>
                                            <span
                                                className={`${adminSurface.statusBadge} ${adminSurface[`status${queueAI.status.toLowerCase().charAt(0).toUpperCase() + queueAI.status.toLowerCase().slice(1).toLowerCase()}`] ?? ""}`}
                                            >
                                                {queueAI.status}
                                            </span>
                                        </td>
                                        <td>
                                            {queueAI.admin?.username ||
                                                queueAI.admin?.email ||
                                                t("notAvailable") ||
                                                "N/A"}
                                        </td>
                                        <td>{getAdditionalInfo(queueAI)}</td>
                                        <td>{getLanguageAI(queueAI)}</td>
                                        <td>{new Date(queueAI.createdAt).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={paginationStyles.controls}>
                        <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                            &lt;
                        </button>
                        {visiblePages.map((p, idx) =>
                            p === "..." ? (
                                <span key={`ellipsis-${idx}`} className="ellipsis">
                                    ...
                                </span>
                            ) : (
                                <button
                                    key={p}
                                    className={p === page ? "active" : ""}
                                    onClick={() => setPage(p as number)}
                                >
                                    {p}
                                </button>
                            ),
                        )}
                        <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                            &gt;
                        </button>
                        <span className="pagination-total">
                            {t("displayedQueueAILogs") || "Displayed logs"}: {`${startItem}–${endItem} / ${totalCount}`}
                        </span>
                    </div>
                </>
            )}
        </div>
    );
};

export default QueueAILogs;
